/**
 * Constants and configuration for PlayerPageHeader components
 *
 * Includes indicator configurations, valuation badge mappings, and display labels.
 * All constants are readonly and centralized for consistency.
 */

import type {
  ChartType,
  IndicatorConfig,
  IndicatorMetricType,
  ValuationBadgeAction,
} from "./playerPageHeaderTypes";

/**
 * Kill switch for the GPR Potential tile and its breakdown route (SE-8777).
 * @remarks Off for the sprint 20 release: the breakdown has no explanation
 * content behind it, so pressing the tile opened an empty screen that blocked
 * the player page. While off, the tile never renders and `gpr-potential` is not
 * a valid breakdown slug. The data path (query fields, utils, tests) stays
 * live so SE-8724 re-enables the card by flipping this one value.
 */
export const GPR_POTENTIAL_ENABLED = false;

/**
 * Empty value placeholder for missing data
 * @remarks Displayed when a field value is null or undefined
 */
export const EMPTY_VALUE_PLACEHOLDER = "—";

/**
 * Indicator configurations keyed by metric type
 * @remarks Contains display labels, descriptions, and chart types for each metric
 */
export const INDICATOR_CONFIGS: Record<IndicatorMetricType, IndicatorConfig> = {
  gpr: {
    type: "gpr",
    label: "GPR",
    shortLabel: "GPR",
    description:
      "The GEMINI Player Rating (GPR) is a proprietary metric that evaluates a player's overall quality and contribution to their team.",
    explanation:
      "GPR combines multiple performance factors including technical ability, physical attributes, tactical awareness, and match impact to produce a single comprehensive score from 0-100.",
    chartType: "teamComparison",
  },
  potential: {
    type: "potential",
    label: "GPR Potential",
    shortLabel: "GPR Potential",
    description:
      "GPR Potential is the range this player's GEMINI Player Rating is projected to reach at peak age.",
    explanation:
      "The floor is the 25th percentile of the projection and the ceiling the 99th, so the range describes a realistic downside and an optimistic upside rather than a single guess. For players already past peak age the projection is retrospective. Coverage is a designed subset — a player without both bounds shows no card at all.",
    chartType: "none",
  },
  playerStyleFit: {
    type: "playerStyleFit",
    label: "Player Style Fit",
    shortLabel: "Player Style Fit",
    description:
      "Player Style Fit measures how well a player's playing style aligns with your organization's defined style of play.",
    explanation:
      "This score is calculated by comparing the player's on-ball and off-ball behaviors against your club's tactical preferences and system requirements. A higher score indicates better alignment.",
    chartType: "teamComparison",
  },
  teamStyleFit: {
    type: "teamStyleFit",
    label: "Team Style Fit",
    shortLabel: "Team Style Fit",
    description:
      "Team Style Fit evaluates how well a player would integrate with your current squad's playing style.",
    explanation:
      "This metric analyzes the player's attributes against your team's collective playing patterns, including passing networks, pressing intensity, and positional play to determine compatibility.",
    chartType: "teamComparison",
  },
  transferValue: {
    type: "transferValue",
    label: "Gemini Player Valuation",
    shortLabel: "Gemini Player Valuation",
    description:
      "The estimated market value range based on GEMINI's proprietary valuation model.",
    explanation:
      "Our valuation model considers age, contract length, performance metrics, market conditions, and comparable transfers to estimate a fair transfer fee range. The bounds represent the likely negotiation range.",
    chartType: "none",
  },
  monthsLeft: {
    type: "monthsLeft",
    label: "Months Left",
    shortLabel: "Months Left",
    description:
      "The remaining duration of the player's current contract in months.",
    explanation:
      "Contract length significantly impacts transfer negotiations and player availability. Players with shorter contracts may be available at reduced fees or on free transfers.",
    chartType: "none",
  },
};

/**
 * Mapping of valuation status strings to badge action types
 * @remarks Determines the visual styling of the valuation badge
 */
export const VALUATION_STATUS_TO_BADGE_ACTION: Record<
  string,
  ValuationBadgeAction
> = {
  SEVERELY_UNDERVALUED: "success",
  "SEVERELY UNDERVALUED": "success",
  UNDERVALUED: "success",
  FAIRLY_VALUED: "info",
  "FAIRLY VALUED": "info",
  OVERVALUED: "warning",
  SEVERELY_OVERVALUED: "error",
  "SEVERELY OVERVALUED": "error",
};

/**
 * Default badge action when valuation status is not recognized
 */
export const DEFAULT_VALUATION_BADGE_ACTION: ValuationBadgeAction = "muted";

/**
 * Get the badge action for a valuation status
 * @param status - The valuation status string from the backend
 * @returns The appropriate badge action for styling
 */
export function getValuationBadgeAction(
  status: string | null | undefined
): ValuationBadgeAction {
  return status
    ? (VALUATION_STATUS_TO_BADGE_ACTION[status] ??
        DEFAULT_VALUATION_BADGE_ACTION)
    : DEFAULT_VALUATION_BADGE_ACTION;
}

/**
 * Get indicator config by metric type
 * @param type - The indicator metric type
 * @returns The configuration for the specified metric type
 */
export function getIndicatorConfig(type: IndicatorMetricType): IndicatorConfig {
  return INDICATOR_CONFIGS[type];
}

/**
 * Get chart type for a metric
 * @param type - The indicator metric type
 * @returns The chart type to display in the detail modal
 */
export function getChartTypeForMetric(type: IndicatorMetricType): ChartType {
  return INDICATOR_CONFIGS[type].chartType;
}
